// Прозорец на „Целия списък": каталогът има 1–3 хил. продукта и рендирането
// на всички редове при всеки клавиш във филтъра се усеща на по-слаба каса.
// Редовете са с ЕДНАКВА височина, затова прозорецът се смята аритметично
// (без зависимост): рендират се само видимите ± OVERSCAN, а празнините отгоре
// и отдолу пазят височината на скрола. Хедърът на колоните е лепнат вътре в
// същия скрол контейнер → височината му се вади от видимата.
//
// Височините се МЕРЯТ в браузъра (първият ред `[role=option]` и хедърът
// `[data-catalog-header]`): редът е 36px с мишка и 44px с пръст (`ROW_DENSE`,
// CSS вариант `pointer-coarse:`) — числата на извикващия са само резерва
// преди първото мерене и в jsdom (там всичко е 0).
import { useCallback, useEffect, useLayoutEffect, useState } from "react";

const OVERSCAN = 8;
// Преди първото измерване (и в jsdom, където няма layout) се приема толкова
const FALLBACK_VIEWPORT_HEIGHT = 640;

export const useCatalogWindow = (rowCount: number, fallbackRowHeight: number, fallbackHeaderHeight: number) => {
  // callback ref: контейнерът се сменя при смяна на подредбата (телефон/десктоп)
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [measured, setMeasured] = useState({ row: 0, header: 0 });

  // При нов контейнер, брой редове или подредба (querySelector е евтин; state
  // се сменя само при разлика)
  useLayoutEffect(() => {
    if (!scrollElement) return;
    const row = scrollElement.querySelector<HTMLElement>('[role="option"]')?.offsetHeight ?? 0;
    const header = scrollElement.querySelector<HTMLElement>("[data-catalog-header]")?.offsetHeight ?? 0;
    // 0 = няма ред/хедър в DOM-а (празен списък) → помни предишното
    setMeasured((current) => {
      const next = { row: row || current.row, header: header || current.header };
      return next.row === current.row && next.header === current.header ? current : next;
    });
  }, [scrollElement, rowCount, fallbackRowHeight]);
  const rowHeight = measured.row || fallbackRowHeight;
  const headerHeight = measured.header || fallbackHeaderHeight;

  useEffect(() => {
    if (!scrollElement) return;
    const measure = () => setViewportHeight(scrollElement.clientHeight);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(scrollElement);
    return () => observer.disconnect();
  }, [scrollElement]);

  const rowsViewport = Math.max(rowHeight, (viewportHeight || FALLBACK_VIEWPORT_HEIGHT) - headerHeight);
  const first = Math.min(rowCount, Math.max(0, Math.floor(scrollTop / rowHeight) - OVERSCAN));
  const last = Math.min(rowCount, Math.ceil((scrollTop + rowsViewport) / rowHeight) + OVERSCAN);
  // PageUp/PageDown прескачат видимото без един ред (за ориентир)
  const pageSize = Math.max(1, Math.floor(rowsViewport / rowHeight) - 1);

  const onScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(event.currentTarget.scrollTop);
  }, []);

  // Маркираният от клавиатурата ред да е изцяло видим — под лепнатия хедър и
  // над долния ръб. Мишката НЕ вика това (редът би избягал изпод курсора).
  const revealRow = useCallback(
    (index: number) => {
      if (!scrollElement || index < 0) return;
      const top = index * rowHeight;
      const visibleHeight = scrollElement.clientHeight - headerHeight;
      if (top < scrollElement.scrollTop) scrollElement.scrollTop = top;
      else if (visibleHeight > 0 && top + rowHeight > scrollElement.scrollTop + visibleHeight)
        scrollElement.scrollTop = top + rowHeight - visibleHeight;
    },
    [scrollElement, rowHeight, headerHeight],
  );

  return {
    setScrollElement,
    onScroll,
    first,
    last,
    topSpacer: first * rowHeight,
    bottomSpacer: Math.max(0, rowCount - last) * rowHeight,
    pageSize,
    revealRow,
  };
};

export type CatalogWindow = ReturnType<typeof useCatalogWindow>;
